"use client";
import { Translate } from "@/components/Translate";

import { Chevron } from "@/components/icons/Chevron";
import { Close24 } from "@/components/icons/Close24";
import { Button } from "@/components/ui/Button";
import {
	PopoverClose,
	PopoverCloseIcon,
	PopoverContent,
	PopoverFooter,
	PopoverRoot,
	PopoverTrigger,
} from "@/components/ui/Popover";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerTitle,
	DrawerTrigger,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { RadioButton, RadioGroup } from "@/components/ui/Radio/Radio";
import { SORT_QUERY_KEY } from "@frend-digital/centra/filters";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { startTransition, useOptimistic, useTransition } from "react";
import styles from "../Filters.module.css";
import { DialogClose } from "@frend-digital/ui";
import type { SortModel } from "@depict-ai/types/api/SearchResponse";
import { withSearchParams } from "@/components/withSearchParams";
import { useProducts } from "../../ListingContext";
import { setTimeout } from "@/utils/timers";

export const SortModal = withSearchParams<{ sortOptions: SortModel[] | undefined }>(
	({ sortOptions, searchParams }) => {
		const { query } = useProducts();
		const selectedRaw = searchParams.get(SORT_QUERY_KEY);
		const defaultSort = sortOptions?.[0];
		const defaultValue = defaultSort
			? `${defaultSort.field}:${defaultSort?.meta?.values?.[0]}`
			: "";
		const selectedSort = selectedRaw || defaultValue;

		const getSortOption = (selectedSort: string) => {
			const [option, order] = selectedSort.split(":");
			return sortOptions?.find(
				(sort) => sort.field === option && sort?.meta?.values?.[0] === order,
			);
		};

		return (
			<>
				<div className="desktop">
					<PopoverRoot>
						<PopoverTrigger className={styles.sort} aria-label="Close sort modal">
							<Chevron />
							<span className="desktop">{getSortOption(selectedSort)?.meta?.title}</span>
						</PopoverTrigger>

						<PopoverContent className={styles.sortPopover}>
							<PopoverCloseIcon>
								<Close24 />
							</PopoverCloseIcon>
							<SortSelect sortOptions={sortOptions} />
							<PopoverFooter>
								<div className={styles.buttons}>
									<PopoverClose asChild>
										<Button style={{ width: "fit-content" }} loading={query.isFetching}>
											<Translate>Show products</Translate>
										</Button>
									</PopoverClose>
								</div>
							</PopoverFooter>
						</PopoverContent>
					</PopoverRoot>
				</div>
				<div className="mobile">
					<DrawerRoot>
						<DrawerTrigger asChild>
							<button type="button" className={styles.filterButton}>
								<Chevron />
								<span>{getSortOption(selectedSort)?.meta?.title}</span>
							</button>
						</DrawerTrigger>
						<DrawerViewport side="right">
							<DrawerClose className={styles.close} />
							<DrawerContent>
								<DrawerHeader className={styles.filterHeader}>
									<DrawerTitle>
										<Translate>Sort by</Translate>
									</DrawerTitle>
								</DrawerHeader>
								<DrawerMain>
									<SortSelect sortOptions={sortOptions} />
								</DrawerMain>
								<DrawerFooter className={styles.footer}>
									<DialogClose asChild>
										<Button style={{ width: "fit-content" }} loading={query.isFetching}>
											<Translate>Show products</Translate>
										</Button>
									</DialogClose>
								</DrawerFooter>
							</DrawerContent>
						</DrawerViewport>
					</DrawerRoot>
				</div>
			</>
		);
	},
);

export const SortSelect = withSearchParams<{
	sortOptions: SortModel[] | undefined;
}>(({ sortOptions, searchParams }) => {
	const defaultSort = sortOptions?.[0];
	const defaultValue = defaultSort ? `${defaultSort.field}:${defaultSort?.meta?.values?.[0]}` : "";
	const [value, setOptimisticValue] = useOptimistic(
		searchParams.get(SORT_QUERY_KEY) || defaultValue,
	);

	const handleValueChange = (selectedValue: unknown) => {
		if (typeof selectedValue !== "string") return;
		startTransition(async () => {
			setOptimisticValue(selectedValue);

			// Set timeout to allow transition of optimistic UI to happend before updating search params
			await setTimeout(1);

			startTransition(() => {
				if (selectedValue === "Standard") {
					searchParams.delete(SORT_QUERY_KEY);
				} else {
					searchParams.set(SORT_QUERY_KEY, selectedValue);
				}
			});
		});
	};

	return (
		<fieldset className={styles.sortFieldset}>
			<legend className={styles.legend}>
				<Translate>Sort by</Translate>
			</legend>
			<RadioGroup
				value={value}
				onValueChange={(value) => {
					handleValueChange(value);
				}}
			>
				{sortOptions?.map((order) => (
					<RadioButton
						key={`${order.field}:${order.meta?.values?.[0]}`}
						label={order?.meta?.title ?? ""}
						value={`${order.field}:${order.meta?.values?.[0]}`}
					/>
				))}
			</RadioGroup>
		</fieldset>
	);
});
